import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { useDealer } from '../../context/DealerContext';
import { dealerApi, CreditRequest, FoundCustomer, RequestQuote } from '../../services/dealerApi';
import CustomerLookup from '../../components/dealer/CustomerLookup';
import QuoteCard from '../../components/dealer/QuoteCard';
import ProformaAcceptDialog from '../../components/dealer/ProformaAcceptDialog';
import CommitSpendNote from '../../components/dealer/CommitSpendNote';
import Money from '../../components/dealer/Money';
import { dealerErrorMessage, fmt, validateWholeAed, NOTE_MAX } from '../../utils/dealer';

/**
 * Spec 085 (Flow B) — request credit for one buyer: find the registered customer by email + phone (BR-8, D-152)
 * → face value → quote → request (proforma DPF-…, the buyer's credit PENDING) → accept the proforma (owner or
 * canCommitSpend staff, BR-6). The request sends the customerRef with the same email + phone; the server re-checks.
 */
export default function RequestCreditPage() {
  const { t } = useLanguage();
  const { token, canCommitSpend } = useDealer();
  const [customer, setCustomer] = useState<FoundCustomer | null>(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [quote, setQuote] = useState<RequestQuote | null>(null);
  const [created, setCreated] = useState<CreditRequest | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    const amountErr = validateWholeAed(amount);
    if (amountErr) errs.amount = t(amountErr);
    if (note.length > NOTE_MAX) errs.note = t('dealer.validation.tooLong');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const getQuote = async (e: FormEvent) => {
    e.preventDefault();
    if (!token || !validate()) return;
    setBusy(true);
    setError(null);
    try {
      setQuote(await dealerApi.quoteRequest(Number(amount), token));
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!token || !quote || !customer || !validate()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await dealerApi.createRequest({
        customerRef: customer.customerRef,
        buyerEmail: customer.email,
        buyerPhone: customer.phone,
        faceValueAed: Number(amount),
        note: note.trim() || undefined,
      }, token);
      setCreated(r);
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    const awaiting = created.status === 'AWAITING_PAYMENT';
    return (
      <div className="dealer-page">
        <div className="dealer-card dealer-success" role="status">
          <h2>{fmt(t('dealer.request.createdTitle'), { number: created.number })}</h2>
          {awaiting ? (
            <>
              <p>{fmt(t('dealer.request.awaitingPayment'), { number: created.number })}</p>
              <p>{t('dealer.quote.totalDue')}: <Money value={created.totalDueAed} /></p>
            </>
          ) : (
            <p>{t('dealer.request.createdBody')}</p>
          )}
          {!awaiting && !canCommitSpend && <CommitSpendNote />}
          <div className="dealer-actions">
            {!awaiting && (
              <button type="button" className="btn btn-primary" disabled={!canCommitSpend} onClick={() => setAccepting(true)}>
                {t('dealer.proforma.accept')}
              </button>
            )}
            <Link className="btn btn-secondary" to="/dealer/credit?tab=requests">{t('dealer.request.viewRequests')}</Link>
          </div>
        </div>
        {accepting && token && (
          <ProformaAcceptDialog
            token={token}
            request={created}
            onClose={() => setAccepting(false)}
            onAccepted={(r) => { setAccepting(false); setCreated(r); }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="dealer-page">
      <div className="dealer-page__header">
        <div>
          <h2>{t('dealer.request.title')}</h2>
          <p className="dealer-muted">{t('dealer.request.subtitle')}</p>
        </div>
      </div>
      <div className="dealer-card">
        <CustomerLookup token={token ?? ''} idPrefix="request-buyer" confirmed={customer}
          onConfirm={setCustomer} onChange={() => { setCustomer(null); setQuote(null); }} />
      </div>
      {customer && (
        <form className="dealer-card dealer-form" onSubmit={getQuote} noValidate>
          <div className="form-group">
            <label htmlFor="face-value">{t('dealer.form.faceValue')}</label>
            <input id="face-value" type="text" inputMode="numeric" dir="ltr" value={amount}
              onChange={(e) => { setAmount(e.target.value); setQuote(null); }} />
            <div className="dealer-hint">{t('dealer.form.faceValueHint')}</div>
            {fieldErrors.amount && <div className="dealer-field-error">{fieldErrors.amount}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="note">{t('dealer.form.note')}</label>
            <input id="note" type="text" value={note} maxLength={NOTE_MAX} placeholder={t('dealer.form.notePlaceholder')}
              onChange={(e) => setNote(e.target.value)} />
            {fieldErrors.note && <div className="dealer-field-error">{fieldErrors.note}</div>}
          </div>
          {error && <div className="error-message">{error}</div>}
          {!quote && (
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? t('dealer.common.loading') : t('dealer.request.getQuote')}
            </button>
          )}
        </form>
      )}
      {customer && quote && (
        <>
          <QuoteCard quote={quote} />
          <div className="dealer-actions">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={submit}>
              {busy ? t('dealer.common.saving') : t('dealer.request.submit')}
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setQuote(null)}>
              {t('dealer.common.edit')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
